/**
 * Small SVG/CSS charts. hq-web has no charting dependency and four screens need
 * the same handful of shapes, so they are drawn here directly: nothing to
 * install, nothing to bundle, colours from the design tokens.
 *
 * Every chart takes real series from the API. None generates data; with no
 * activity they draw an axis and say so.
 */

import type { ReactNode } from "react";
import { cn } from "@/lib/cn";
import { formatNumber, formatShortDate, formatHour, WEEKDAYS } from "./format";
import styles from "./insights.module.css";

export const CHART_COLORS = {
  primary: "var(--color-primary)",
  accent: "var(--color-accent)",
  info: "var(--color-info)",
  warning: "var(--color-warning)",
  danger: "var(--color-danger)",
  muted: "var(--color-gray-400)",
} as const;

export interface ChartSeries {
  key: string;
  label: string;
  color: string;
  values: number[];
}

// ---------------------------------------------------------------------------
// Axis helpers
// ---------------------------------------------------------------------------

/** Round a maximum up to a tidy axis ceiling (1, 2, 5, 10, 20, 50, ...). */
export function niceCeil(max: number): number {
  if (max <= 0) return 1;
  if (max <= 4) return Math.ceil(max);
  const pow = Math.pow(10, Math.floor(Math.log10(max)));
  for (const step of [1, 2, 5, 10]) {
    if (max <= step * pow) return step * pow;
  }
  return 10 * pow;
}

function yTicks(top: number): number[] {
  if (top <= 4) return Array.from({ length: top + 1 }, (_, i) => i);
  return [0, top / 2, top];
}

function xLabelIndexes(count: number, max = 6): number[] {
  if (count <= max) return Array.from({ length: count }, (_, i) => i);
  const step = (count - 1) / (max - 1);
  return Array.from({ length: max }, (_, i) => Math.round(i * step));
}

const W = 640;
const PAD = { left: 34, right: 8, top: 10, bottom: 22 };

interface FrameProps {
  labels: string[];
  top: number;
  height: number;
  formatX?: (label: string) => string;
  empty: boolean;
  emptyText: string;
  children: ReactNode;
}

function Frame({ labels, top, height, formatX = formatShortDate, empty, emptyText, children }: FrameProps) {
  const plotH = height - PAD.top - PAD.bottom;
  const ticks = yTicks(top);
  return (
    <>
      {ticks.map((t) => {
        const y = PAD.top + plotH - (t / top) * plotH;
        return (
          <g key={t}>
            <line x1={PAD.left} x2={W - PAD.right} y1={y} y2={y} className={styles.gridLine} />
            <text x={PAD.left - 6} y={y + 3} textAnchor="end" className={styles.axisText}>
              {formatNumber(t)}
            </text>
          </g>
        );
      })}
      {children}
      {xLabelIndexes(labels.length).map((i) => {
        const band = (W - PAD.left - PAD.right) / Math.max(labels.length, 1);
        const x = PAD.left + band * i + band / 2;
        return (
          <text key={i} x={x} y={height - 6} textAnchor="middle" className={styles.axisText}>
            {formatX(labels[i] ?? "")}
          </text>
        );
      })}
      {empty ? (
        <text x={W / 2} y={PAD.top + plotH / 2} textAnchor="middle" className={styles.emptyChartText}>
          {emptyText}
        </text>
      ) : null}
    </>
  );
}

export function Legend({ series }: { series: Pick<ChartSeries, "key" | "label" | "color">[] }) {
  return (
    <div className={styles.legend}>
      {series.map((s) => (
        <span key={s.key} className={styles.legendItem}>
          <span className={styles.legendDot} style={{ background: s.color }} aria-hidden />
          {s.label}
        </span>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Bar chart (grouped or stacked)
// ---------------------------------------------------------------------------

export interface BarChartProps {
  labels: string[];
  series: ChartSeries[];
  stacked?: boolean;
  height?: number;
  ariaLabel: string;
  emptyText?: string;
  formatX?: (label: string) => string;
}

export function BarChart({
  labels,
  series,
  stacked = false,
  height = 200,
  ariaLabel,
  emptyText = "Nothing recorded in this range",
  formatX,
}: BarChartProps) {
  const n = labels.length;
  const totals = labels.map((_, i) =>
    stacked
      ? series.reduce((s, x) => s + (x.values[i] ?? 0), 0)
      : Math.max(0, ...series.map((x) => x.values[i] ?? 0)),
  );
  const max = Math.max(0, ...totals);
  const top = niceCeil(max);
  const plotW = W - PAD.left - PAD.right;
  const plotH = height - PAD.top - PAD.bottom;
  const band = plotW / Math.max(n, 1);
  const inner = Math.min(band * 0.72, 28);

  return (
    <svg viewBox={`0 0 ${W} ${height}`} className={styles.chart} role="img" aria-label={ariaLabel}>
      <Frame
        labels={labels}
        top={top}
        height={height}
        empty={max === 0}
        emptyText={emptyText}
        {...(formatX ? { formatX } : {})}
      >
        {labels.map((label, i) => {
          const cx = PAD.left + band * i + band / 2;
          if (stacked) {
            let acc = 0;
            return (
              <g key={label + i}>
                {series.map((s) => {
                  const v = s.values[i] ?? 0;
                  if (v <= 0) return null;
                  const h = (v / top) * plotH;
                  const y = PAD.top + plotH - ((acc + v) / top) * plotH;
                  acc += v;
                  return (
                    <rect key={s.key} x={cx - inner / 2} y={y} width={inner} height={h} fill={s.color} rx={2}>
                      <title>{`${label}: ${s.label} ${formatNumber(v)}`}</title>
                    </rect>
                  );
                })}
              </g>
            );
          }
          const w = inner / series.length;
          return (
            <g key={label + i}>
              {series.map((s, k) => {
                const v = s.values[i] ?? 0;
                if (v <= 0) return null;
                const h = (v / top) * plotH;
                return (
                  <rect
                    key={s.key}
                    x={cx - inner / 2 + k * w}
                    y={PAD.top + plotH - h}
                    width={Math.max(w - 1, 1)}
                    height={h}
                    fill={s.color}
                    rx={2}
                  >
                    <title>{`${label}: ${s.label} ${formatNumber(v)}`}</title>
                  </rect>
                );
              })}
            </g>
          );
        })}
      </Frame>
    </svg>
  );
}

// ---------------------------------------------------------------------------
// Line chart
// ---------------------------------------------------------------------------

export interface LineChartProps {
  labels: string[];
  series: ChartSeries[];
  height?: number;
  ariaLabel: string;
  emptyText?: string;
  area?: boolean;
  formatX?: (label: string) => string;
}

export function LineChart({
  labels,
  series,
  height = 200,
  ariaLabel,
  emptyText = "Nothing recorded in this range",
  area = true,
  formatX,
}: LineChartProps) {
  const n = labels.length;
  const max = Math.max(0, ...series.flatMap((s) => s.values));
  const top = niceCeil(max);
  const plotW = W - PAD.left - PAD.right;
  const plotH = height - PAD.top - PAD.bottom;
  const band = plotW / Math.max(n, 1);
  const px = (i: number) => PAD.left + band * i + band / 2;
  const py = (v: number) => PAD.top + plotH - (v / top) * plotH;

  return (
    <svg viewBox={`0 0 ${W} ${height}`} className={styles.chart} role="img" aria-label={ariaLabel}>
      <Frame
        labels={labels}
        top={top}
        height={height}
        empty={max === 0}
        emptyText={emptyText}
        {...(formatX ? { formatX } : {})}
      >
        {series.map((s) => {
          if (s.values.length === 0) return null;
          const pts = s.values.map((v, i) => `${px(i)},${py(v)}`).join(" ");
          const baseline = PAD.top + plotH;
          return (
            <g key={s.key}>
              {area && n > 1 ? (
                <polygon
                  points={`${px(0)},${baseline} ${pts} ${px(n - 1)},${baseline}`}
                  fill={s.color}
                  opacity={0.12}
                />
              ) : null}
              <polyline points={pts} fill="none" stroke={s.color} strokeWidth={2} strokeLinejoin="round" />
              {s.values.map((v, i) =>
                v > 0 || n <= 14 ? (
                  <circle key={i} cx={px(i)} cy={py(v)} r={n > 40 ? 1.5 : 3} fill={s.color}>
                    <title>{`${labels[i]}: ${s.label} ${formatNumber(v)}`}</title>
                  </circle>
                ) : null,
              )}
            </g>
          );
        })}
      </Frame>
    </svg>
  );
}

// ---------------------------------------------------------------------------
// Sparkline
// ---------------------------------------------------------------------------

export function Sparkline({
  values,
  color = CHART_COLORS.primary,
  height = 32,
  ariaLabel,
}: {
  values: number[];
  color?: string;
  height?: number;
  ariaLabel: string;
}) {
  const width = 120;
  const max = Math.max(0, ...values);
  if (values.length < 2 || max === 0) {
    return (
      <svg viewBox={`0 0 ${width} ${height}`} className={styles.chart} role="img" aria-label={`${ariaLabel}: no activity`}>
        <line x1={0} x2={width} y1={height - 2} y2={height - 2} className={styles.gridLine} />
      </svg>
    );
  }
  const step = width / (values.length - 1);
  const pts = values.map((v, i) => `${i * step},${height - 2 - (v / max) * (height - 6)}`).join(" ");
  return (
    <svg viewBox={`0 0 ${width} ${height}`} className={styles.chart} role="img" aria-label={ariaLabel}>
      <polyline points={pts} fill="none" stroke={color} strokeWidth={1.75} strokeLinejoin="round" />
    </svg>
  );
}

// ---------------------------------------------------------------------------
// Funnel
// ---------------------------------------------------------------------------

export function Funnel({ steps }: { steps: { key: string; label: string; count: number }[] }) {
  const first = steps[0]?.count ?? 0;
  return (
    <div role="list" style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      {steps.map((step, i) => {
        const prev = i === 0 ? null : steps[i - 1]!.count;
        const ofFirst = first > 0 ? step.count / first : 0;
        const stepRate = prev !== null && prev > 0 ? step.count / prev : null;
        return (
          <div key={step.key} role="listitem" className={styles.funnelRow}>
            <span>{step.label}</span>
            <div className={styles.funnelTrack} aria-hidden>
              <div className={styles.funnelBar} style={{ width: `${Math.round(ofFirst * 100)}%` }} />
            </div>
            <span className={styles.funnelValue}>
              {formatNumber(step.count)}
              {first > 0 ? ` · ${Math.round(ofFirst * 100)}%` : ""}
              {stepRate !== null ? (
                <span className={styles.funnelDrop}>{`  (${Math.round(stepRate * 100)}% of previous step)`}</span>
              ) : null}
            </span>
          </div>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Horizontal bars
// ---------------------------------------------------------------------------

export interface HBarItem {
  label: string;
  value: number;
  display?: string;
}

export function HBars({ items, empty = "Nothing to show yet" }: { items: HBarItem[]; empty?: string }) {
  if (items.length === 0) return <p className={styles.note}>{empty}</p>;
  const max = Math.max(1, ...items.map((i) => i.value));
  return (
    <div role="list" style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      {items.map((item) => (
        <div key={item.label} role="listitem" className={styles.hbarRow}>
          <span className={styles.hbarLabel} title={item.label}>
            {item.label}
          </span>
          <div className={styles.hbarTrack} aria-hidden>
            <div className={styles.hbarFill} style={{ width: `${(item.value / max) * 100}%` }} />
          </div>
          <span className={styles.hbarValue}>{item.display ?? formatNumber(item.value)}</span>
        </div>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Day-of-week x hour heatmap
// ---------------------------------------------------------------------------

export function Heatmap({ cells }: { cells: { dow: number; hour: number; n: number }[] }) {
  const max = Math.max(0, ...cells.map((c) => c.n));
  const lookup = new Map(cells.map((c) => [`${c.dow}-${c.hour}`, c.n]));
  const hours = Array.from({ length: 24 }, (_, h) => h);
  return (
    <div className={styles.heatWrap}>
      <div className={styles.heat} role="img" aria-label="Bookings by weekday and hour of the booked start time">
        <span />
        {hours.map((h) => (
          <span key={h} className={styles.heatHour}>
            {h % 3 === 0 ? formatHour(h) : ""}
          </span>
        ))}
        {WEEKDAYS.map((day, d) => (
          <Row key={day} day={day} dow={d + 1} hours={hours} lookup={lookup} max={max} />
        ))}
      </div>
    </div>
  );
}

function Row({
  day,
  dow,
  hours,
  lookup,
  max,
}: {
  day: string;
  dow: number;
  hours: number[];
  lookup: Map<string, number>;
  max: number;
}) {
  return (
    <>
      <span className={styles.heatDay}>{day}</span>
      {hours.map((h) => {
        const n = lookup.get(`${dow}-${h}`) ?? 0;
        return (
          <span
            key={h}
            className={styles.heatCell}
            style={n > 0 ? { background: "var(--color-primary)", opacity: 0.2 + 0.8 * (n / max) } : undefined}
            title={`${day} ${formatHour(h)}: ${n} booking${n === 1 ? "" : "s"}`}
          />
        );
      })}
    </>
  );
}

// ---------------------------------------------------------------------------
// Retention cohort table
// ---------------------------------------------------------------------------

export function RetentionTable({ cohorts }: { cohorts: { week: string; size: number; retained: number[] }[] }) {
  const shown = cohorts.filter((c) => c.size > 0);
  if (shown.length === 0) {
    return <p className={styles.note}>No member has signed up in the last 8 weeks, so there is no cohort to follow yet.</p>;
  }
  const columns = Math.max(...shown.map((c) => c.retained.length));
  return (
    <div style={{ overflowX: "auto" }}>
      <table className={styles.retention}>
        <thead>
          <tr>
            <th scope="col">Signed up (week of)</th>
            <th scope="col">Members</th>
            {Array.from({ length: columns }, (_, k) => (
              <th key={k} scope="col">{k === 0 ? "Week 0" : `+${k}`}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {shown.map((c) => (
            <tr key={c.week}>
              <td>{formatShortDate(c.week)}</td>
              <td>{formatNumber(c.size)}</td>
              {Array.from({ length: columns }, (_, k) => {
                const value = c.retained[k];
                if (value === undefined) return <td key={k} />;
                const share = c.size > 0 ? value / c.size : 0;
                return (
                  <td
                    key={k}
                    className={styles.retentionCell}
                    style={{ background: `rgba(0, 56, 101, ${0.06 + share * 0.5})` }}
                    title={`${value} of ${c.size} members active`}
                  >
                    {Math.round(share * 100)}%
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ---------------------------------------------------------------------------
// KPI tile, stat, meter, banner
// ---------------------------------------------------------------------------

export function Delta({ current, previous, label }: { current: number; previous: number; label: string }) {
  if (previous === 0 && current === 0) return <span className={cn(styles.kpiMeta, styles.deltaFlat)}>No change</span>;
  if (previous === 0) return <span className={cn(styles.kpiMeta, styles.deltaUp)}>{`New · none in ${label}`}</span>;
  const pct = Math.round(((current - previous) / previous) * 100);
  const tone = pct > 0 ? styles.deltaUp : pct < 0 ? styles.deltaDown : styles.deltaFlat;
  const arrow = pct > 0 ? "▲" : pct < 0 ? "▼" : "•";
  return <span className={cn(styles.kpiMeta, tone)}>{`${arrow} ${Math.abs(pct)}% vs ${label}`}</span>;
}

export interface KpiProps {
  label: string;
  value: ReactNode;
  meta?: ReactNode;
  spark?: ReactNode;
  onClick?: () => void;
  /** Permission denied for this slice: shows a lock message, not a number. */
  denied?: boolean;
  /** Highlights the value as needing attention. */
  attention?: boolean;
  small?: boolean;
}

export function Kpi({ label, value, meta, spark, onClick, denied = false, attention = false, small = false }: KpiProps) {
  const body = (
    <>
      <span className={styles.kpiLabel}>{label}</span>
      {denied ? (
        <span className={styles.kpiDenied}>No access with your role</span>
      ) : (
        <>
          <span className={cn(styles.kpiValue, small && styles.kpiValueSmall)}>{value}</span>
          {meta ? <span className={styles.kpiMeta}>{meta}</span> : null}
          {spark}
        </>
      )}
    </>
  );
  if (onClick && !denied) {
    return (
      <button type="button" className={cn(styles.kpi, attention && styles.kpiAlert)} onClick={onClick}>
        {body}
      </button>
    );
  }
  return <div className={cn(styles.kpi, attention && styles.kpiAlert)}>{body}</div>;
}

export function KpiGrid({ children }: { children: ReactNode }) {
  return <div className={styles.kpiGrid}>{children}</div>;
}

export function TwoCol({ children }: { children: ReactNode }) {
  return <div className={styles.twoCol}>{children}</div>;
}

export function Stat({ label, value, note }: { label: string; value: ReactNode; note?: ReactNode }) {
  return (
    <div className={styles.stat}>
      <span className={styles.statValue}>{value}</span>
      <span className={styles.statLabel}>{label}</span>
      {note ? <span className={styles.note}>{note}</span> : null}
    </div>
  );
}

export function StatGrid({ children }: { children: ReactNode }) {
  return <div className={styles.statGrid}>{children}</div>;
}

export function Meter({ fraction, label }: { fraction: number | null; label: string }) {
  const pct = fraction === null ? 0 : Math.max(0, Math.min(1, fraction)) * 100;
  return (
    <div className={styles.meterTrack} role="img" aria-label={label}>
      <div className={styles.meterFill} style={{ width: `${pct}%` }} />
    </div>
  );
}

export function Note({ children }: { children: ReactNode }) {
  return <p className={styles.note}>{children}</p>;
}

export function EnvBanner({
  kind,
  label,
  projectRef,
  extra,
}: {
  kind: "development" | "production" | "unknown";
  label: string;
  projectRef: string | null;
  extra?: ReactNode;
}) {
  const tone = kind === "production" ? styles.envProd : kind === "development" ? styles.envDev : styles.envUnknown;
  return (
    <div className={cn(styles.envBanner, tone)} role="status">
      <span className={styles.envTag}>{label}</span>
      <span>
        {kind === "production"
          ? "You are looking at live production data."
          : kind === "development"
            ? "You are looking at the DEV database. Nothing here is customer data."
            : "The API could not identify which database it is connected to."}
      </span>
      <span className={styles.note}>{projectRef ? `Supabase project ${projectRef}` : "Project reference unavailable"}</span>
      {extra}
    </div>
  );
}
